import type {
  KpiCardData,
  ContractStatusData,
  OrderRowData,
  ProjectStageData,
  ProjectStatusData,
  ProjectStatusDetailStatus,
  ProjectStatusEndorsement,
  ProjectStatusExpandedDetails,
  StatusBadgeVariant,
  SupportStatusData,
  PartnerProgramData,
} from "../../../features/partner-dashboard/types";
import type { IntegrationMilestoneState } from "../../../pages/hid-admin/dashboard/partner-organizations/partner-list/integration-status/integrationStatusTypes";
import type {
  ApiIntegrationTimeline,
  ApiPartnerIntegration,
  ApiTimelinePhase,
} from "../integration-status/Types";
import { mapPhaseToMilestoneState, isNotApplicableStatus, NOT_APPLICABLE_META_LABEL } from "../../../common/components/integration-timeline/timelineUtils";
import { mapTechnologyToBadge } from "../../../utils/technologyBadge";
import { formatConsultantName } from "../../../utils/formatConsultantName";
import {
  hasActivePartnerContract,
  NO_SUPPORT_CONTRACT,
  normalizeSupportLevel,
} from "../../../features/partner-dashboard/constants/supportLevel";
import type {
  PartnerDashboardContractStatus,
  PartnerDashboardOrderStat,
  PartnerDashboardProjectStats,
  PartnerDashboardSummary,
} from "./Types";

const STATIC_ORDER_CATEGORIES = ["License", "Service", "Renewal"];

const formatDisplayValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return String(value);
};

const formatKpiValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "";
  }
  return String(value);
};

const formatKpiNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return value.toLocaleString("en-US");
};

const formatPartnerDuration = (value: string | null | undefined): string => {
  const formatted = formatKpiValue(value);
  if (!formatted) {
    return "";
  }

  return formatted.replace(/(\d+)\s+mo\b/g, (_, count) => {
    const n = Number(count);
    return `${count} ${n === 1 ? "month" : "months"}`;
  });
};

export const mapSummaryToKpiCards = (
  summary: PartnerDashboardSummary,
  completedIntegrations: number
): KpiCardData[] => [
  {
    value: formatKpiValue(summary.partner_since),
    label: "Partner Since",
    subtext: formatPartnerDuration(summary.partner_duration),
  },
  {
    value: formatKpiValue(summary.partner_tier),
    label: "Partner Program",
    // subtext: "",
    subtext: `${formatKpiNumber(summary.tier_score)} pts`,
  },
  {
    value: formatKpiNumber(completedIntegrations),
    label: "Completed Integrations",
    subtext: "",
  },
];

export const mapSupportStatusFromSummary = (
  summary: PartnerDashboardSummary | null | undefined
): SupportStatusData => ({
  canSupport: summary?.support_status === 1,
  supportLevel: normalizeSupportLevel(summary?.support_level),
});

export const mapSummaryToPartnerProgram = (
  summary: PartnerDashboardSummary
): PartnerProgramData => {
  const currentPoints = summary.tier_score ?? 0;
  const nextTierPoints = summary.next_tier_points ?? 0;
  const progressPercent =
    nextTierPoints > 0
      ? Math.min(100, Math.round((currentPoints / nextTierPoints) * 100))
      : 0;

  return {
    tier: formatDisplayValue(summary.partner_tier),
    currentPoints,
    nextTierPoints,
    progressPercent,
  };
};

export const PLACEHOLDER_PARTNER_PROGRAM: PartnerProgramData = {
  tier: "-",
  currentPoints: 0,
  nextTierPoints: 0,
  progressPercent: 0,
};

export const PLACEHOLDER_KPI_CARDS: KpiCardData[] = [
  { value: "", label: "Partner Since", subtext: "" },
  { value: "", label: "Partner Program", subtext: "" },
  { value: "", label: "Completed Integrations", subtext: "" },
];

const formatContractStatusLabel = (
  status: string | null | undefined
): string => {
  const value = formatDisplayValue(status);
  if (value === "-") {
    return "-";
  }
  if (value.toLowerCase().includes("contract")) {
    return value;
  }
  return `${value} Contract`;
};

const formatRenewalSubtext = (
  renewalDue: string | null | undefined
): string => {
  const value = formatKpiValue(renewalDue);
  if (!value) {
    return "";
  }
  return `Renews ${value}`;
};

export const mapActiveContractKpi = (
  contractStatus: PartnerDashboardContractStatus
): KpiCardData => {
  const hasContract = hasActivePartnerContract(contractStatus);
  const contractStatusLabel = formatContractStatusLabel(
    contractStatus.contract_status
  );

  return {
    label: hasContract ? contractStatusLabel : NO_SUPPORT_CONTRACT,
    value: hasContract ? formatDisplayValue(contractStatus.contract_name) : "",
    subtext: hasContract ? formatRenewalSubtext(contractStatus.renewal_due) : "",
  };
};

export const PLACEHOLDER_ACTIVE_CONTRACT_KPI: KpiCardData = {
  label: NO_SUPPORT_CONTRACT,
  value: "",
  subtext: "",
};

const formatContractDate = (value: string | null | undefined): string => {
  if (!value || value === "-") {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-US", {
    month: "short",
    year: "numeric",
  });
};

export const mapContractStatusToCardData = (
  contractStatus: PartnerDashboardContractStatus
): ContractStatusData => {
  const percentElapsed = contractStatus.percent_elapsed ?? 0;

  return {
    renewalDueLabel: "RENEWAL DUE",
    renewalDueDate: formatDisplayValue(contractStatus.renewal_due),
    timeLeft: formatDisplayValue(contractStatus.time_left),
    progressPercent: percentElapsed,
    progressLabel: `${percentElapsed}% of contract term elapsed`,
    lastRenewed: formatContractDate(contractStatus.last_renewed),
    lastExpired: formatContractDate(contractStatus.last_expired),
  };
};

export const PLACEHOLDER_CONTRACT_STATUS: ContractStatusData = {
  renewalDueLabel: "RENEWAL DUE",
  renewalDueDate: "-",
  timeLeft: "-",
  progressPercent: 0,
  progressLabel: "-",
  lastRenewed: "-",
  lastExpired: "-",
};

const formatOrderDate = (value: string | null | undefined): string => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatOrderAmount = (value: string | null | undefined): string => {
  if (!value) {
    return "-";
  }

  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return value;
  }

  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const mapOrderStatus = (
  status: string
): {
  label: string;
  variant: Extract<StatusBadgeVariant, "paid" | "invoiced" | "unfulfilled">;
} => {
  const normalized = status.trim().toUpperCase();

  if (normalized === "PAID" || normalized === "FULFILLED") {
    return { label: "Paid", variant: "paid" };
  }

  if (normalized === "INVOICED") {
    return { label: "Invoiced", variant: "invoiced" };
  }

  if (normalized === "UNFULFILLED") {
    return { label: "Unfulfilled", variant: "unfulfilled" };
  }

  const label = status
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

  return { label, variant: "unfulfilled" };
};

export const mapRecentOrdersToCardData = (
  orders: PartnerDashboardOrderStat[]
): OrderRowData[] =>
  orders.slice(0, 3).map((order, index) => {
    const status = mapOrderStatus(order.status);

    return {
      id: formatDisplayValue(order.order_number),
      category: STATIC_ORDER_CATEGORIES[index % STATIC_ORDER_CATEGORIES.length],
      description: formatDisplayValue(order.item),
      date: formatOrderDate(order.date),
      amount: formatOrderAmount(order.amount),
      statusLabel: status.label,
      statusVariant: status.variant,
    };
  });

const normalizeTechnologyName = (technology: string): string => {
  const lower = technology.toLowerCase().trim();

  if (lower.startsWith("apple wallet")) {
    return "Apple Wallet";
  }

  if (lower.startsWith("google wallet")) {
    return "Google Wallet";
  }

  if (lower.startsWith("samsung wallet")) {
    return "Samsung Wallet";
  }

  return technology.trim();
};

const formatTimelineDate = (date: string | null | undefined): string | undefined => {
  if (!date || date.trim() === "" || date.startsWith("0000-")) {
    return undefined;
  }

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime()) || parsed.getFullYear() <= 0) {
    return undefined;
  }

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const mapPhaseToStage = (
  stageKey: "onboarding" | "training" | "development" | "validation",
  label: string,
  phase: ApiTimelinePhase,
  options?: { treatPendingAsNotApplicable?: boolean }
): ProjectStageData => {
  const state = mapPhaseToMilestoneState(phase.status);
  const completed = state === "completed";
  const completionDate = formatTimelineDate(phase.completion_date);
  const statusDate =
    state === "in-progress" || state === "on-hold"
      ? formatTimelineDate(phase.start_date) ?? completionDate
      : undefined;

  const showNotApplicable =
    state === "pending" &&
    (isNotApplicableStatus(phase.status) ||
      (Boolean(options?.treatPendingAsNotApplicable) &&
        stageKey !== "validation" &&
        !completionDate));

  return {
    stage: stageKey,
    label,
    completed,
    state,
    ...(completed && completionDate ? { date: completionDate } : {}),
    ...(statusDate ? { date: statusDate } : {}),
    ...(showNotApplicable ? { statusLabel: NOT_APPLICABLE_META_LABEL } : {}),
  };
};


const mapIntegrationStatusDetail = (
  integrationStatus: string
): ProjectStatusDetailStatus => {
  const lower = integrationStatus.toLowerCase().trim();

  if (lower === "completed") {
    return { label: "Completed", variant: "completed" };
  }

  if (lower === "not applicable") {
    return { label: "Not Applicable", variant: "not-applicable" };
  }

  if (lower === "not completed") {
    return { label: "Not Completed", variant: "not-completed" };
  }

  return {
    label: integrationStatus.trim() || "—",
    variant: "not-completed",
  };
};

const isNotApplicableIntegration = (integration: ApiPartnerIntegration): boolean => {
  const lower = integration.integration_status.toLowerCase().trim();
  return lower === "not applicable" || lower === "notapplicable";
};

const mapValidationStatusDetail = (
  timeline: ApiIntegrationTimeline
): ProjectStatusDetailStatus => {
  const status = timeline.validation?.status?.trim() ?? "";

  if (!status) {
    return { label: "Not Started", variant: "not-started" };
  }

  const lower = status.toLowerCase();

  if (lower === "completed") {
    return { label: "Completed", variant: "completed" };
  }

  if (lower === "on hold") {
    return { label: "On Hold", variant: "on-hold" };
  }

  if (lower === "in progress") {
    return { label: "In Progress", variant: "in-progress" };
  }

  if (lower === "not started") {
    return { label: "Not Started", variant: "not-started" };
  }

  if (lower === "not applicable") {
    return { label: "Not Applicable", variant: "not-applicable" };
  }

  return { label: status, variant: "not-completed" };
};

const mapEndorsementDetail = (endorsement: string): ProjectStatusEndorsement => {
  const trimmed = endorsement.trim();

  if (!trimmed || trimmed === "-") {
    return { label: "—", variant: "neutral" };
  }

  if (trimmed.toLowerCase() === "fully endorsed") {
    return { label: "Fully Endorsed", variant: "success" };
  }

  if (trimmed.toLowerCase() === "not endorsed") {
    return { label: "Not Endorsed", variant: "danger" };
  }

  return { label: trimmed, variant: "neutral" };
};

const mapExpandedDetails = (
  integration: ApiPartnerIntegration
): ProjectStatusExpandedDetails => ({
  integrationStatus: mapIntegrationStatusDetail(integration.integration_status),
  validationStatus: mapValidationStatusDetail(integration.timeline),
  age: integration.age?.trim() || "—",
  endorsement: mapEndorsementDetail(integration.endorsement),
  score: integration.score ?? 0,
});

const mapIntegrationToProjectStatus = (
  integration: ApiPartnerIntegration,
  projectName: string,
  technology: string,
  consultantName?: string
): ProjectStatusData => {
  const timeline = integration.timeline;
  const validationCompleted =
    (timeline.validation?.status || "").toLowerCase().trim() === "completed";
  const stageOptions = {
    treatPendingAsNotApplicable: validationCompleted,
  };
  const timelineStages: ProjectStageData[] = [
    mapPhaseToStage(
      "onboarding",
      "Onboarding",
      timeline.onboarding,
      stageOptions
    ),
    mapPhaseToStage("training", "Training", timeline.training, stageOptions),
    mapPhaseToStage(
      "development",
      "Development",
      timeline.development,
      stageOptions
    ),
    mapPhaseToStage(
      "validation",
      "Validation",
      timeline.validation,
      stageOptions
    ),
  ];

  const completedCount = timelineStages.filter((stage) => stage.completed).length;
  const completionPercent = validationCompleted
    ? 100
    : Math.round((completedCount / timelineStages.length) * 100);
  const normalizedTechnology = normalizeTechnologyName(technology);
  const badge = mapTechnologyToBadge(normalizedTechnology);

  return {
    id: String(integration.integration_id),
    name: projectName,
    technology: normalizedTechnology,
    technologyFilter: normalizedTechnology,
    subLabel: integration.integration_name,
    technicalConsultant: consultantName,
    completionPercent,
    stages: timelineStages,
    expandedDetails: mapExpandedDetails(integration),
    ...badge,
  };
};

export type ProjectStatusDashboardData = {
  projects: ProjectStatusData[];
  projectFilters: string[];
};

export const mapProjectStatsToDashboard = (
  projectStats: PartnerDashboardProjectStats | null | undefined
): ProjectStatusDashboardData => {
  if (!projectStats?.data?.length) {
    return { projects: [], projectFilters: ["All"] };
  }

  const partner = projectStats.data[0];
  const projects: ProjectStatusData[] = [];

  partner.technologies.forEach((tech) => {
    const normalizedTechnology = normalizeTechnologyName(tech.technology);

    tech.projects.forEach((project) => {
      project.integrations.forEach((integration) => {
        if (isNotApplicableIntegration(integration)) {
          return;
        }

        projects.push(
          mapIntegrationToProjectStatus(
            integration,
            project.project_name,
            normalizedTechnology,
            formatConsultantName(project.consultant_details)
          )
        );
      });
    });
  });

  const technologyFilters =
    projectStats.technologies_stats?.map((stat) =>
      normalizeTechnologyName(stat.technology)
    ) ?? [];

  const uniqueFilters = technologyFilters.filter(
    (technology, index) => technologyFilters.indexOf(technology) === index
  );

  if (uniqueFilters.length === 0) {
    const fromProjects = projects.map((project) => project.technologyFilter);
    uniqueFilters.push(
      ...fromProjects.filter(
        (technology, index) => fromProjects.indexOf(technology) === index
      )
    );
  }

  return {
    projects,
    projectFilters: ["All", ...uniqueFilters],
  };
};

export const EMPTY_PROJECT_STATUS: ProjectStatusDashboardData = {
  projects: [],
  projectFilters: ["All"],
};
